import axios from "axios";
import { IS_EMERGENCY, IS_ANONYMOUS } from "../common/const";

/**
	* @typedef {"one_time" | "weekly" | "monthly" | "yearly"} ScheduledRepeatType
	*/

/**
 * @typedef {Object} CreateScheduledMessageDto
 * @property {string} [userGroupId] - Optional user group ID
 * @property {string} title - Message title
 * @property {string} message - Message content
 * @property {string} toPhoneNumber - Target phone number in E.164 format
 * @property {string} scheduledAt - ISO date string for when to send the message (must be at least 5 minutes in the future)
 * @property {string[]} [reminders] - Optional array of reminder delays (e.g. ["7d", "24h", "1h", "30m"])
	* @property {string} locale - Optional
	* @property {ScheduledRepeatType} repeatType - Message recurrence type
 */

/**
 * @typedef {Object} UpdateScheduledMessageDto
 * @property {string} [userGroupId] - Optional user group ID
 * @property {string} [title] - Message title
 * @property {string} [message] - Message content
 * @property {string} [scheduledAt] - ISO date string for when to send the message (must be at least 5 minutes in the future)
 * @property {string[]} [reminders] - Array of reminder delays (e.g. ["7d", "24h", "1h", "30m"])
	* @property {string} locale - Optional
	* @property {ScheduledRepeatType} [repeatType] - Optional Message recurrence type
 */

/**
 * @typedef {Object} ScheduledMessagesView
 * @property {string} id - Unique identifier
 * @property {string} title - Message title
 * @property {Date} scheduledAt - When the message is scheduled to be sent
 * @property {Date} shortestDelay - Earliest reminder time
 * @property {string} toPhoneNumber - Target phone number
 * @property {Date} lastStatusAt - Timestamp of the last status update
 * @property {string} lastStatusMessage - Last status message
 * @property {string} message - Message content
 * @property {string[]} reminders - Array of reminder delays
 * @property {string} status - Current message status
	* @property {string} locale - Optional
	* @property {ScheduledRepeatType} repeatType - Message recurrence type
 */

const scheduleMessageService = {
	/**
     * Schedule a new message
     * @param {CreateScheduledMessageDto} scheduledMessage - The message data to create
     * @returns {Promise<ScheduledMessagesView>} The created scheduled message
     * @throws {Error} If creation fails
     */
	async create(scheduledMessage) {
		try {
			const response = await axios.post("scheduled-messages", scheduledMessage);

			return response.data;
		} catch (error) {
			console.log("Error creating scheduled message:", error);
			throw error;
		}
	},

	/**
     * Get all scheduled messages for the current organization
     * @returns {Promise<ScheduledMessagesView[]>} Array of scheduled messages
     * @throws {Error} If fetching fails
     */
	async getAll() {
		try {
			const response = await axios.get("scheduled-messages");

			return response.data;
		} catch (error) {
			console.log("Error fetching scheduled messages:", error);
			throw error;
		}
	},

	/**
     * Get a specific scheduled message by ID
     * @param {string} messageId - The ID of the scheduled message
     * @returns {Promise<ScheduledMessagesView>} The scheduled message
     * @throws {Error} If fetching fails
     */
	async getById(messageId) {
		try {
			const response = await axios.get(`scheduled-messages/${messageId}`);

			return response.data;
		} catch (error) {
			console.log("Error fetching scheduled message:", error);
			throw error;
		}
	},

	/**
     * Update a scheduled message
     * @param {string} messageId - The ID of the scheduled message to update
     * @param {UpdateScheduledMessageDto} updateData - The data to update
     * @returns {Promise<ScheduledMessagesView>} The updated scheduled message
     * @throws {Error} If update fails
     */
	async update(messageId, updateData) {
		try {
			const response = await axios.put(`scheduled-messages/${messageId}`, updateData);

			return response.data;
		} catch (error) {
			console.log("Error updating scheduled message:", error);
			throw error;
		}
	},

	/**
     * Cancel/delete a scheduled message
     * @param {string} messageId - The ID of the scheduled message to remove
     * @returns {Promise<void>}
     * @throws {Error} If deletion fails
     */
	async remove(messageId) {
		try {
			const response = await axios.delete(`scheduled-messages/${messageId}`);

			return response.data;
		} catch (error) {
			console.log("Error removing scheduled message:", error);
			throw error;
		}
	},

	/**
     * Get all scheduled messages for a specific phone number
     * @param {string} phone - The phone number to search for
     * @returns {Promise<ScheduledMessagesView[]>} Array of scheduled messages for the phone number
     * @throws {Error} If fetching fails
     */
	async findByPhone(phone) {
		if (!IS_EMERGENCY(phone) && !IS_ANONYMOUS(phone)) {
			try {
				const response = await axios.get(`scheduled-messages/phone/${phone}`);

				return response.data;
			} catch (error) {
				console.log("Error fetching scheduled messages by phone:", error);
				throw error;
			}
		}
	}
};

export default scheduleMessageService;
